import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Briefcase, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManageCareers = () => {
  const { language } = useLanguage();
  const [careers, setCareers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    status: 'open',
    description: '',
    requirements: '',
    is_active: true
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [careersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/careers?active_only=false`),
        axios.get(`${API}/contact-settings`)
      ]);
      setCareers(careersRes.data || []);
      const cats = settingsRes.data?.career_categories || ['Construction', 'IT & Digital', 'General'];
      setCategories(cats);
    } catch (err) {
      toast.error(language === 'en' ? 'Failed to load careers' : 'Gagal memuat lowongan');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({
      title: '',
      category: categories[0] || 'General',
      status: 'open',
      description: '',
      requirements: '',
      is_active: true
    });
    setDialogOpen(true);
  };

  const openEdit = (career) => {
    setEditing(career);
    setFormData({
      title: career.title || '',
      category: career.category || categories[0] || 'General',
      status: career.status || 'open',
      description: career.description || '',
      requirements: career.requirements || '',
      is_active: career.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/careers/${editing.id}`, formData);
        toast.success(language === 'en' ? 'Position updated' : 'Posisi diperbarui');
      } else {
        await axios.post(`${API}/careers`, formData);
        toast.success(language === 'en' ? 'Position created' : 'Posisi dibuat');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'en' ? 'Failed to save' : 'Gagal menyimpan'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (career) => {
    if (!window.confirm(language === 'en' ? `Delete "${career.title}"?` : `Hapus "${career.title}"?`)) return;
    try {
      await axios.delete(`${API}/careers/${career.id}`);
      toast.success(language === 'en' ? 'Position deleted' : 'Posisi dihapus');
      fetchAll();
    } catch (err) {
      toast.error(language === 'en' ? 'Failed to delete' : 'Gagal menghapus');
    }
  };

  if (loading) {
    return (
      <AdminLayout title={language === 'en' ? 'Manage Careers' : 'Kelola Karir'}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={language === 'en' ? 'Manage Careers' : 'Kelola Karir'}>
      <div data-testid="manage-careers-page">
        <div className="flex items-center justify-between mb-6">
          <p className="text-stone-600">
            {language === 'en'
              ? 'Create and manage open positions displayed on the public Careers page.'
              : 'Buat dan kelola posisi yang ditampilkan di halaman Karir publik.'}
          </p>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-career-btn">
            <Plus className="w-4 h-4 mr-2" />
            {language === 'en' ? 'New Position' : 'Posisi Baru'}
          </Button>
        </div>

        {careers.length === 0 ? (
          <Card data-testid="careers-empty">
            <CardContent className="py-16 text-center">
              <Briefcase className="w-12 h-12 mx-auto text-stone-300 mb-4" />
              <h3 className="font-semibold text-lg text-stone-700 mb-2">
                {language === 'en' ? 'No positions yet' : 'Belum ada posisi'}
              </h3>
              <p className="text-stone-500 mb-6">
                {language === 'en' ? 'Create your first job opening.' : 'Buat lowongan pekerjaan pertama Anda.'}
              </p>
              <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                {language === 'en' ? 'New Position' : 'Posisi Baru'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {careers.map((career) => (
              <Card key={career.id} data-testid={`career-card-${career.id}`}>
                <CardContent className="py-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className="font-heading font-semibold text-lg text-stone-900">{career.title}</h3>
                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-medium">
                          {career.category}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          career.status === 'open' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'
                        }`}>
                          {career.status === 'open'
                            ? (language === 'en' ? 'Open' : 'Dibuka')
                            : (language === 'en' ? 'Closed' : 'Ditutup')}
                        </span>
                        {!career.is_active && (
                          <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs font-medium">
                            {language === 'en' ? 'Hidden' : 'Disembunyikan'}
                          </span>
                        )}
                      </div>
                      {career.description && (
                        <p className="text-sm text-stone-600 line-clamp-2">{career.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => openEdit(career)} data-testid={`edit-career-${career.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(career)}
                        data-testid={`delete-career-${career.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Form Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="career-form-dialog">
            <DialogHeader>
              <DialogTitle>
                {editing
                  ? (language === 'en' ? 'Edit Position' : 'Edit Posisi')
                  : (language === 'en' ? 'New Position' : 'Posisi Baru')}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div>
                <Label>{language === 'en' ? 'Position Title' : 'Judul Posisi'} *</Label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={language === 'en' ? 'e.g., Senior Software Engineer' : 'mis., Senior Software Engineer'}
                  className="mt-1"
                  data-testid="career-title-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'en' ? 'Category' : 'Kategori'} *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(val) => setFormData({ ...formData, category: val })}
                  >
                    <SelectTrigger className="mt-1" data-testid="career-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-stone-500 mt-1">
                    {language === 'en' ? 'Manage categories in Company Settings' : 'Kelola kategori di Pengaturan Perusahaan'}
                  </p>
                </div>
                <div>
                  <Label>{language === 'en' ? 'Status' : 'Status'} *</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(val) => setFormData({ ...formData, status: val })}
                  >
                    <SelectTrigger className="mt-1" data-testid="career-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">{language === 'en' ? 'Open' : 'Dibuka'}</SelectItem>
                      <SelectItem value="closed">{language === 'en' ? 'Closed' : 'Ditutup'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>{language === 'en' ? 'Description' : 'Deskripsi'}</Label>
                <Textarea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={language === 'en' ? 'Brief overview of the role...' : 'Ringkasan singkat tentang posisi...'}
                  className="mt-1"
                  data-testid="career-description-input"
                />
              </div>

              <div>
                <Label>{language === 'en' ? 'Requirements' : 'Persyaratan'}</Label>
                <Textarea
                  rows={5}
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  placeholder={language === 'en'
                    ? '- Bachelor degree in...\n- 3+ years of experience...\n- Strong skills in...'
                    : '- Gelar sarjana di...\n- 3+ tahun pengalaman...\n- Keterampilan kuat dalam...'}
                  className="mt-1 font-mono text-sm"
                  data-testid="career-requirements-input"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-md">
                <div>
                  <Label>{language === 'en' ? 'Visible on website' : 'Terlihat di website'}</Label>
                  <p className="text-xs text-stone-500 mt-1">
                    {language === 'en' ? 'Toggle off to hide without deleting' : 'Matikan untuk menyembunyikan tanpa menghapus'}
                  </p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(val) => setFormData({ ...formData, is_active: val })}
                  data-testid="career-active-switch"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                  {language === 'en' ? 'Cancel' : 'Batal'}
                </Button>
                <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="career-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (language === 'en' ? 'Save' : 'Simpan')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ManageCareers;
